import React from 'react';
import AiIcon from '~/assets/icons/ai.svg';
import type { LocalizedField, AiLoadingKey } from './types';
import { matchesAiLoadingKey } from './types';
import { AutoResizeTextarea } from './AutoResizeTextarea';
import {
  FieldCell,
  FieldLabel,
  GenerateButton,
  MissingBadge,
  ReadonlyBox,
  SectionRow,
  SpinnerDot,
  StaleBadge,
  TargetFieldHeader
} from './ScriptTranslationView.styles';

export type TranslationFieldProps = {
  label: string;
  field: LocalizedField | null | undefined;
  path: readonly (string | number)[];
  aiLoadingKey: AiLoadingKey | null;
  fieldAiKey: AiLoadingKey;
  aiLoading: boolean;
  hasPendingChanges: boolean;
  shouldShowField: (source?: string | null, target?: string | null) => boolean;
  onUpdate: (path: readonly (string | number)[], value: string) => void;
  onSave: () => void;
  onGenerateAi: () => void;
};

export const TranslationField: React.FC<TranslationFieldProps> = ({
  label,
  field,
  path,
  aiLoadingKey,
  fieldAiKey,
  aiLoading,
  hasPendingChanges,
  shouldShowField,
  onUpdate,
  onSave,
  onGenerateAi
}) => {
  const isThisLoading = matchesAiLoadingKey(aiLoadingKey, fieldAiKey);
  const { source, target } = field || {};
  const hasSource = !!(source && source.trim().length > 0);

  const renderSourceCell = () =>
    hasSource ? (
      <FieldCell>
        <TargetFieldHeader>
          <FieldLabel>{label}</FieldLabel>
        </TargetFieldHeader>
        <ReadonlyBox>{source}</ReadonlyBox>
      </FieldCell>
    ) : (
      <FieldCell />
    );

  const showGenerateButton =
    field?.missing || field?.stale || !(target && target.trim().length > 0);

  return (
    <SectionRow>
      {renderSourceCell()}
      {shouldShowField(source, target) ? (
        <FieldCell>
          <TargetFieldHeader>
            <FieldLabel>
              {label}
              {field?.missing && <MissingBadge>Missing</MissingBadge>}
              {field?.stale && <StaleBadge>Outdated</StaleBadge>}
            </FieldLabel>
            {showGenerateButton && (
              <GenerateButton
                type="button"
                onClick={onGenerateAi}
                disabled={aiLoading}
              >
                {isThisLoading ? <SpinnerDot /> : <AiIcon />}
                {isThisLoading ? 'Translating…' : 'Generate Translation'}
              </GenerateButton>
            )}
          </TargetFieldHeader>
          <AutoResizeTextarea
            value={target ?? ''}
            onChange={e => onUpdate(path, e.target.value)}
            onBlur={() => {
              if (hasPendingChanges) onSave();
            }}
            placeholder="Type here..."
          />
        </FieldCell>
      ) : (
        <FieldCell />
      )}
    </SectionRow>
  );
};
